import type { ReactNode } from "react";
import { useEffect } from "react";
import { Button } from "./ui";

interface Props {
	/** The world's width over its height. Fixed per game, never per screen. */
	aspect: number;
	score: number;
	friendName: string;
	friendScore: number | null;
	/** Paused while the child is deciding whether to give up. */
	paused: boolean;
	onPause: () => void;
	onResume: () => void;
	/** Ends the run here, and sends up what they earned before they stopped. */
	onGiveUp: () => void;
	/** What the run has to say once it is over, if anything. */
	notice: string | null;
	controls: ReactNode;
	children: ReactNode;
}

/**
 * The whole screen, for as long as a run lasts.
 *
 * Everything a room normally shows — the code, the player cards, the reactions,
 * how to play — is about two children taking turns, and an arcade run is one
 * child and a board. Keeping the room frame around it cost most of the glass
 * and bought nothing, so a run takes the screen and the room comes back the
 * moment it ends.
 */
export function ArcadeStage({
	aspect,
	score,
	friendName,
	friendScore,
	paused,
	onPause,
	onResume,
	onGiveUp,
	notice,
	controls,
	children,
}: Props) {
	useNoScrollBehind();
	useFullScreenWhereOffered();

	return (
		<div className="arcade-stage" style={{ ["--play-aspect" as string]: `${aspect}` }}>
			<header className="flex shrink-0 items-center gap-3 px-1">
				{/*
				 * Top left, small, and away from where a thumb rests — on the flying
				 * game the whole board is the flap button, so a big target up here
				 * would be a way of quitting by accident at sixty steps a second.
				 */}
				<button
					type="button"
					aria-label="Pause"
					onPointerDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
						onPause();
					}}
					className="rounded-xl bg-white/10 px-3 py-1.5 text-lg leading-none"
				>
					<span aria-hidden="true">⏸</span>
				</button>

				{/* The number is all a player needs to see; a live region reading
				    out "40" on its own tells a screen reader nothing at all. */}
				<p data-testid="arcade-score" role="status" className="text-2xl font-black tabular-nums">
					<span className="sr-only">Score </span>
					{score}
				</p>

				{friendScore !== null && (
					<p className="ml-auto text-base font-semibold opacity-70">
						{friendName} {friendScore}
					</p>
				)}
			</header>

			<div className="arcade-fit">{children}</div>

			<footer className="shrink-0">
				{notice ? (
					<p role="status" className="py-4 text-center text-xl font-bold">
						{notice}
					</p>
				) : (
					controls
				)}
			</footer>

			{paused && (
				<div className="absolute inset-0 z-10 grid place-items-center bg-slate-950/80 p-6">
					<div className="text-center">
						<h2 className="text-3xl font-black">Paused</h2>
						<p className="mt-2 text-lg opacity-80">
							Your go is still going — nothing has been lost.
						</p>
						<div className="mt-6 flex flex-wrap justify-center gap-3">
							<Button size="lg" onClick={onResume}>
								▶️ Keep playing
							</Button>
							<Button size="lg" tone="secondary" onClick={onGiveUp}>
								🏳️ Give up
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

/**
 * Stops the room behind the stage scrolling while a game is on.
 *
 * The stage is fixed, so a stray drag would move the page underneath it rather
 * than doing nothing — which on a phone reads as the game having jumped.
 */
function useNoScrollBehind() {
	useEffect(() => {
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previous;
		};
	}, []);
}

/**
 * Asks for real full screen, where the browser has such a thing.
 *
 * Worth a few lines because on Android it is the difference between a game and
 * a game with an address bar over it. iOS Safari does not offer it for anything
 * but video and will simply refuse, which is fine — the stage already covers
 * the viewport, and this only ever removes the browser's own chrome on top.
 * Never retried and never insisted upon: a refusal here is not a failure.
 */
function useFullScreenWhereOffered() {
	useEffect(() => {
		const root = document.documentElement;
		if (!root.requestFullscreen || document.fullscreenElement) return;
		void root.requestFullscreen().catch(() => {
			// No full screen here. The stage covers the page either way.
		});
		return () => {
			if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
		};
	}, []);
}
